/**
 * Paging contract with sales-coach-core.
 * @module @shared/constants/paging
 */

/**
 * Server's default per-request ceiling (`PagedQueryDto` `@Max(100)`).
 * `SkillsPagedQueryDto` / `ObservableSignalsPagedQueryDto` override this to
 * 1000 server-side; the web client does not enforce that override itself —
 * a caller that exceeds it is meant to see the server's own HTTP 400 rather
 * than have the value silently truncated here.
 */
export const API_MAX_PAGE_SIZE = 100;

/** Server's default when `limit` is omitted (`PagedQueryDto` `limit = 20`). */
export const API_DEFAULT_PAGE_SIZE = 20;

/** First page number; the server is 1-indexed (`PagedQueryDto` `@Min(1)`). */
export const FIRST_PAGE = 1;

/**
 * Page size for lists the user pages or scrolls through. Distinct from
 * {@link API_MAX_PAGE_SIZE}: this is a UI choice, that is a hard server bound.
 */
export const UI_PAGE_SIZE = 20;

/** Rows fetched for a searchable picker; the search box narrows server-side beyond this. */
export const PICKER_PAGE_SIZE = 50;

/** Page size for card grids, where a row holds 4 and 6 rows fill a screen. */
export const UI_GRID_PAGE_SIZE = 24;

/** Rows-per-page choices for admin tables. Default 10 keeps the pager on screen. */
export const UI_TABLE_PAGE_SIZE_OPTIONS = [10, 20, 50] as const;
export const UI_TABLE_PAGE_SIZE_DEFAULT: (typeof UI_TABLE_PAGE_SIZE_OPTIONS)[number] = 10;

/** Wait after the last keystroke before querying server-side search. */
export const SEARCH_DEBOUNCE_MS = 300;

/** Shape every paged endpoint returns. */
export interface PagedMetaLike {
  total: number;
  page: number;
  limit: number;
  totalPages: number;
  hasNextPage?: boolean;
  hasPrevPage?: boolean;
}

/** Generic paged response envelope — the one shape new list endpoints return. */
export interface PagedResponse<T> {
  data: T[];
  meta: PagedMetaLike;
}

interface PagedLike<T> {
  data: T[];
  meta?: Partial<PagedMetaLike>;
}

export interface FetchAllPagesProgress {
  page: number;
  loadedItems: number;
  totalPages?: number;
  totalItems?: number;
}

export interface FetchAllPagesOptions {
  pageSize: number;
  maxPages: number;
  maxItems: number;
  signal?: AbortSignal;
  onProgress?(progress: FetchAllPagesProgress): void;
}

/**
 * Bounded profile for reference/lookup data (skills, stages, channels,
 * insurance types, persona categories/items, and similarly small, bounded
 * lookups). Not for interactive pickers over large/unbounded lists — those
 * belong on a paged or infinite query instead.
 */
export const FETCH_ALL_REFERENCE_OPTIONS: FetchAllPagesOptions = {
  pageSize: 100,
  maxPages: 10,
  maxItems: 1_000,
};

/**
 * Bounded profile for bulk export flows. If the server-reported total exceeds
 * `maxItems`, the helper throws rather than returning a file silently missing
 * rows — the caller should ask the user to narrow their filter.
 */
export const FETCH_ALL_EXPORT_OPTIONS: FetchAllPagesOptions = {
  pageSize: 100,
  maxPages: 1_000,
  maxItems: 100_000,
};

/**
 * Reads every page of a paged endpoint and returns the rows as one array.
 *
 * For callers that genuinely need the whole bounded set (exports, lookup
 * maps, reference-data pickers). Requesting an oversized `limit` instead does
 * not work: see {@link API_MAX_PAGE_SIZE}.
 *
 * Loop is strictly sequential — page N+1 is only requested after page N
 * resolves — because parallel offset fetches (`Promise.all`) can duplicate or
 * skip rows when the underlying set is mutated mid-export. Never parallelize
 * this loop.
 *
 * Stop conditions, checked in order each iteration:
 * 1. Abort is checked immediately before and immediately after the network
 *    call.
 * 2. If `meta.total` is reported and exceeds `maxItems`, throws immediately
 *    (even on page 1) — a caller must not silently receive a partial export.
 * 3. If accumulating this batch would exceed `maxItems`, throws.
 * 4. `meta.hasNextPage === false` -> return what has been collected.
 * 5. `page >= meta.totalPages` -> return what has been collected.
 * 6. Short-page fallback: only when the response carries no authoritative
 *    meta (`hasNextPage` boolean absent AND `totalPages` number absent) and
 *    the batch came back shorter than `pageSize`, treat that as the end.
 * 7. If authoritative meta is present but the batch is empty while meta still
 *    claims more pages exist, that is a server inconsistency — throw rather
 *    than returning a partial list.
 *
 * Falling through all `maxPages` iterations without any stop condition
 * firing throws, since a paging bug must not spin forever.
 *
 * Returns `T[]` only — it never fabricates a meta object for the caller.
 */
export async function fetchAllPages<T>(
  fetchPage: (page: number, limit: number, signal?: AbortSignal) => Promise<PagedLike<T>>,
  options: FetchAllPagesOptions
): Promise<T[]> {
  const { pageSize, maxPages, maxItems, signal, onProgress } = options;
  const rows: T[] = [];

  for (let page = FIRST_PAGE; page < FIRST_PAGE + maxPages; page += 1) {
    signal?.throwIfAborted();
    const response = await fetchPage(page, pageSize, signal);
    signal?.throwIfAborted();

    const batch = response.data ?? [];
    const serverTotal = response.meta?.total;

    if (typeof serverTotal === 'number' && serverTotal > maxItems) {
      throw new Error(
        `fetchAllPages: server reports ${serverTotal} items, exceeding the ${maxItems} limit; refusing to fetch a partial set.`
      );
    }
    if (rows.length + batch.length > maxItems) {
      throw new Error(
        `fetchAllPages exceeded the ${maxItems}-item limit; refusing to return a partial list.`
      );
    }

    rows.push(...batch);
    onProgress?.({
      page,
      loadedItems: rows.length,
      totalPages: response.meta?.totalPages,
      totalItems: serverTotal,
    });

    if (response.meta?.hasNextPage === false) return rows;
    if (typeof response.meta?.totalPages === 'number' && page >= response.meta.totalPages) {
      return rows;
    }

    const hasAuthoritativeMeta =
      typeof response.meta?.hasNextPage === 'boolean' ||
      typeof response.meta?.totalPages === 'number';
    if (!hasAuthoritativeMeta && batch.length < pageSize) return rows;
    if (hasAuthoritativeMeta && batch.length === 0) {
      throw new Error(
        `fetchAllPages: page ${page} returned no rows but meta still indicates more pages exist (inconsistent paging).`
      );
    }
  }

  throw new Error(
    `fetchAllPages exceeded ${maxPages} pages (${pageSize} per page); refusing to return a partial list.`
  );
}
